import {useEffect, useState} from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Row,
  Spin,
  Typography,
  Select,
  message,
} from 'antd';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {Curriculum, Program, Level} from '../../graphql/API';
import {CurriculumsService} from './CurriculumsService';
import {ProgramsNewService} from '../Program';
import {LevelsService} from '../Level/LevelsService';
import {PlacementTestsService} from '../PlacementTest/PlacementTestsService';
import {CurriculumTypeEnum} from '@PikaDoApp/npm-pikado-logic';

const curriculumsService = new CurriculumsService();
const programsService = new ProgramsNewService();
const levelsService = new LevelsService();
const placementTestsService = new PlacementTestsService();

export const CurriculumEdit = () => {
  const history = useHistory();
  const location: {
    state: any;
    hash: any;
    search: any;
    pathname: any;
  } = useLocation();

  const {Title, Text} = Typography;
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  let params: {curriculumId: string} = useParams();
  const [curriculum, setCurriculum] = useState<Curriculum>();

  // State for dropdown options
  const [programs, setPrograms] = useState<Array<Program> | []>([]);
  const [levels, setLevels] = useState<Array<Level> | []>([]);
  const [placementTests, setPlacementTests] = useState<Array<any> | []>([]);

  // State for selected values
  const [selectedProgramId, setSelectedProgramId] = useState<string>();
  const [selectedLevelIds, setSelectedLevelIds] = useState<string[]>([]);
  const [selectedPlacementTestIds, setSelectedPlacementTestIds] = useState<
    string[]
  >([]);
  const [selectedCurriculumType, setSelectedCurriculumType] =
    useState<string>();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        // Fetch curriculum
        const fetchedCurriculum = await curriculumsService.getCurriculumById(
          params.curriculumId,
        );
        setCurriculum(fetchedCurriculum);

        // Fetch programs
        const fetchedPrograms = await programsService.getNewPrograms();
        setPrograms(fetchedPrograms);

        // Fetch ALL available levels for dropdown options
        const fetchedLevels = await levelsService.getLevels();
        setLevels(fetchedLevels);

        // Fetch placement tests
        const fetchedPlacementTests =
          await placementTestsService.getPlacementTests(1, 100);
        setPlacementTests(fetchedPlacementTests.items || []);

        // Set selected values from curriculum
        if (fetchedCurriculum) {
          // Set curriculum type
          if (fetchedCurriculum.type) {
            setSelectedCurriculumType(fetchedCurriculum.type);
          }

          // Set program ID
          if (fetchedCurriculum.program) {
            setSelectedProgramId(fetchedCurriculum.program);
          }

          // Set level IDs - map curriculum level IDs to fetched level IDs
          if (
            fetchedCurriculum.level &&
            Array.isArray(fetchedCurriculum.level)
          ) {
            // Map curriculum level _ids to fetched level ids
            const levelIds = fetchedCurriculum.level
              .map((currLevel: any) => {
                // Find matching level in fetched levels by _id or oldId
                const matchedLevel = fetchedLevels.find(
                  (l: any) =>
                    l.id === currLevel._id || l.oldId === currLevel._id,
                );
                return matchedLevel?.id;
              })
              .filter((id: string | undefined) => id !== undefined);

            setSelectedLevelIds(levelIds as string[]);
          }

          // Set placement test IDs - extract _id directly from curriculum's placement test objects
          if (
            fetchedCurriculum.placementTest &&
            Array.isArray(fetchedCurriculum.placementTest)
          ) {
            // The API returns full placement test objects with _id
            // Simply extract the _id values to match the dropdown's value field
            const testIds = fetchedCurriculum.placementTest
              .map((currTest: any) => currTest._id || currTest.id)
              .filter((id: string | undefined) => id !== undefined);

            setSelectedPlacementTestIds(testIds as string[]);
          }
        }
      } catch (error) {
        console.log(
          '🔍 [CURRICULUM-NEW-PACKAGE] Edit page - error loading data:',
          error,
        );
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.curriculumId]);

  const onChange = (prop: any, value: any) => {
    //@ts-ignore
    setCurriculum({...curriculum, [prop]: value});
  };
  //console.log('membership', curriculum);
  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Edit Curriculum </Title>

      {isLoading && (
        <div style={{textAlign: 'center', padding: '50px 0'}}>
          <Spin size="large" tip="Loading curriculum..." />
        </div>
      )}

      {isError && (
        <Alert
          message="Error"
          description="Failed to load curriculum. Please try again."
          type="error"
          showIcon
          style={{marginBottom: 20}}
        />
      )}

      {!isLoading && !isError && curriculum && (
        <>
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Curriculum Name
                </Text>
                <Input
                  placeholder="Enter Curriculum Name"
                  onChange={event => onChange('name', event.target.value)}
                  value={curriculum?.name}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Curriculum Type *
                </Text>
                <Select
                  value={selectedCurriculumType}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select Curriculum Type"
                  optionFilterProp="children"
                  onChange={type => {
                    setSelectedCurriculumType(type);
                    onChange('type', type);
                  }}>
                  {Object.values(CurriculumTypeEnum).map(type => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Select Curriculum Program *
                </Text>
                <Select
                  value={selectedProgramId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a Program"
                  optionFilterProp="children"
                  onChange={programId => setSelectedProgramId(programId)}>
                  {programs?.map(prgm => (
                    <Select.Option key={prgm.id} value={prgm.id!}>
                      {prgm.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Select Curriculum Levels *
                </Text>
                <Select
                  mode="multiple"
                  value={selectedLevelIds}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select levels"
                  optionFilterProp="children"
                  onChange={levelIds => setSelectedLevelIds(levelIds)}>
                  {levels?.map((level: any) => (
                    <Select.Option key={level.id} value={level.id}>
                      {level.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Select Placement Tests
                </Text>
                <div style={{display: 'flex', gap: 10}}>
                  <Select
                    mode="multiple"
                    value={selectedPlacementTestIds}
                    showSearch
                    style={{flex: 1}}
                    placeholder="Select placement tests"
                    optionFilterProp="children"
                    onChange={testIds => setSelectedPlacementTestIds(testIds)}>
                    {placementTests?.map(test => (
                      <Select.Option
                        key={test._id || test.id}
                        value={test._id || test.id}>
                        {test.name}
                      </Select.Option>
                    ))}
                  </Select>
                  <Button
                    onClick={() => history.push('/placement-tests/create')}
                    type="default">
                    create
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <Button
            type="primary"
            loading={isSaving}
            disabled={isSaving}
            onClick={async () => {
              // Validate required fields
              if (
                !curriculum?.name ||
                !selectedCurriculumType ||
                !selectedProgramId ||
                !selectedLevelIds ||
                selectedLevelIds.length === 0
              ) {
                message.error(
                  'Please fill in all required fields including curriculum type and at least one level',
                );
                return;
              }

              setIsSaving(true);
              try {
                // NEW IMPLEMENTATION - Using @PikaDoApp/npm-pikado-logic package
                await curriculumsService.updateNewCurriculum(
                  params.curriculumId,
                  curriculum,
                  selectedProgramId,
                  selectedLevelIds,
                  selectedPlacementTestIds,
                  selectedCurriculumType as CurriculumTypeEnum,
                );
                message.success('Curriculum updated successfully!');
                history.push('/curriculums');
              } catch (error) {
                console.error(
                  '🔍 [CURRICULUM-NEW-PACKAGE] Update error:',
                  error,
                );
                message.error('Failed to update curriculum. Please try again.');
              } finally {
                setIsSaving(false);
              }
            }}>
            Update Curriculum
          </Button>
        </>
      )}
    </div>
  );
};
